import { useState, ChangeEvent, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import {
  Strategy,
  useCreateStrategyMutation,
  useListStrategiesQuery,
} from "features/trading/accounts";
import { StrategyGroupEnum } from "constants/index";
import AssetAutocomplete from "../AssetAutocomplete";

interface CreateStrategyModalProps {
  open: boolean;
  onClose: () => void;
  strategyGroupId: number;
}

interface StrategyFormData {
  name: string;
  asset: number | "";
  strategy_group: number;
  current_call: number | null;
  previous_call: number | null;
  current_call_price: string;
  hedge_ratio: string;
  target_delta: string;
}

const createInitialFormData = (strategyGroupId: number): StrategyFormData => ({
  name: "",
  asset: "",
  strategy_group: strategyGroupId,
  current_call: null,
  previous_call: null,
  current_call_price: "0",
  hedge_ratio: "0.80",
  target_delta: "0.30",
});

const resolveCallOrPut = (
  strategyGroupId: number,
  existingStrategy?: Strategy,
): boolean => {
  if (existingStrategy) {
    return existingStrategy.call_or_put;
  }
  return strategyGroupId === StrategyGroupEnum.HEDGE;
};

const CreateStrategyModal: React.FC<CreateStrategyModalProps> = ({
  open,
  onClose,
  strategyGroupId,
}) => {
  const [formData, setFormData] = useState(() =>
    createInitialFormData(strategyGroupId),
  );

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      strategy_group: strategyGroupId,
    }));
  }, [strategyGroupId]);

  const [createStrategy] = useCreateStrategyMutation();
  const { data: strategiesData } = useListStrategiesQuery({
    pagination: { page: 0, pageSize: 1 },
    filters: { strategy_group: strategyGroupId },
  });

  const handleClose = () => {
    setFormData(createInitialFormData(strategyGroupId));
    onClose();
  };

  const handleInputChange =
    (field: keyof StrategyFormData) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleAssetChange = (assetId: number | "") => {
    setFormData((prev) => ({ ...prev, asset: assetId }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createStrategy({
        ...formData,
        asset: Number(formData.asset),
        current_call_price: formData.current_call_price,
        call_or_put: resolveCallOrPut(
          strategyGroupId,
          strategiesData?.results?.[0],
        ),
        strategy_group: strategyGroupId,
      }).unwrap();
      handleClose();
    } catch (error) {
      console.error("Failed to create strategy:", error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Position</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                label="Position Name"
                type="text"
                fullWidth
                value={formData.name}
                onChange={handleInputChange("name")}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <AssetAutocomplete
                value={formData.asset}
                onChange={handleAssetChange}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Hedge Ratio"
                type="number"
                fullWidth
                value={formData.hedge_ratio}
                onChange={handleInputChange("hedge_ratio")}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Target Delta"
                type="number"
                fullWidth
                value={formData.target_delta}
                onChange={handleInputChange("target_delta")}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formData.name.trim() || !formData.asset}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateStrategyModal;
